// i18next configuration for the extension.
// All locale resources are bundled statically (explicit imports so it works in
// both the webpack build and CRA jest) — t() is synchronous and ready before
// first paint, important under MV3 CSP and to avoid a translation flash.
// Language is auto-detected (see detect.ts); there is no runtime switcher.

import i18n, { Resource } from "i18next";
import { initReactI18next } from "react-i18next";

import { detectLocale, DEFAULT_LOCALE, SUPPORTED_LOCALES } from "./detect";
import { applyDocumentLocale } from "./applyDocumentLocale";

import en_common from "./locales/en/common.json";
import en_quran from "./locales/en/quran.json";
import en_todos from "./locales/en/todos.json";
import en_notes from "./locales/en/notes.json";
import en_weather from "./locales/en/weather.json";
import en_prayer from "./locales/en/prayer.json";
import en_account from "./locales/en/account.json";
import en_auth from "./locales/en/auth.json";
import en_tour from "./locales/en/tour.json";
import en_location from "./locales/en/location.json";
// barakah (Barakah Blocks planner): every locale ships its own file; keys a
// locale has not translated yet resolve through fallbackLng (English).
import en_barakah from "./locales/en/barakah.json";
import ar_common from "./locales/ar/common.json";
import ar_quran from "./locales/ar/quran.json";
import ar_todos from "./locales/ar/todos.json";
import ar_notes from "./locales/ar/notes.json";
import ar_weather from "./locales/ar/weather.json";
import ar_prayer from "./locales/ar/prayer.json";
import ar_account from "./locales/ar/account.json";
import ar_auth from "./locales/ar/auth.json";
import ar_tour from "./locales/ar/tour.json";
import ar_location from "./locales/ar/location.json";
import ar_barakah from "./locales/ar/barakah.json";
import ur_common from "./locales/ur/common.json";
import ur_quran from "./locales/ur/quran.json";
import ur_todos from "./locales/ur/todos.json";
import ur_notes from "./locales/ur/notes.json";
import ur_weather from "./locales/ur/weather.json";
import ur_prayer from "./locales/ur/prayer.json";
import ur_account from "./locales/ur/account.json";
import ur_auth from "./locales/ur/auth.json";
import ur_tour from "./locales/ur/tour.json";
import ur_location from "./locales/ur/location.json";
import ur_barakah from "./locales/ur/barakah.json";
import fa_common from "./locales/fa/common.json";
import fa_quran from "./locales/fa/quran.json";
import fa_todos from "./locales/fa/todos.json";
import fa_notes from "./locales/fa/notes.json";
import fa_weather from "./locales/fa/weather.json";
import fa_prayer from "./locales/fa/prayer.json";
import fa_account from "./locales/fa/account.json";
import fa_auth from "./locales/fa/auth.json";
import fa_tour from "./locales/fa/tour.json";
import fa_location from "./locales/fa/location.json";
import fa_barakah from "./locales/fa/barakah.json";
import id_common from "./locales/id/common.json";
import id_quran from "./locales/id/quran.json";
import id_todos from "./locales/id/todos.json";
import id_notes from "./locales/id/notes.json";
import id_weather from "./locales/id/weather.json";
import id_prayer from "./locales/id/prayer.json";
import id_account from "./locales/id/account.json";
import id_auth from "./locales/id/auth.json";
import id_tour from "./locales/id/tour.json";
import id_location from "./locales/id/location.json";
import id_barakah from "./locales/id/barakah.json";
import tr_common from "./locales/tr/common.json";
import tr_quran from "./locales/tr/quran.json";
import tr_todos from "./locales/tr/todos.json";
import tr_notes from "./locales/tr/notes.json";
import tr_weather from "./locales/tr/weather.json";
import tr_prayer from "./locales/tr/prayer.json";
import tr_account from "./locales/tr/account.json";
import tr_auth from "./locales/tr/auth.json";
import tr_tour from "./locales/tr/tour.json";
import tr_location from "./locales/tr/location.json";
import tr_barakah from "./locales/tr/barakah.json";
import fr_common from "./locales/fr/common.json";
import fr_quran from "./locales/fr/quran.json";
import fr_todos from "./locales/fr/todos.json";
import fr_notes from "./locales/fr/notes.json";
import fr_weather from "./locales/fr/weather.json";
import fr_prayer from "./locales/fr/prayer.json";
import fr_account from "./locales/fr/account.json";
import fr_auth from "./locales/fr/auth.json";
import fr_tour from "./locales/fr/tour.json";
import fr_location from "./locales/fr/location.json";
import fr_barakah from "./locales/fr/barakah.json";
import bn_common from "./locales/bn/common.json";
import bn_quran from "./locales/bn/quran.json";
import bn_todos from "./locales/bn/todos.json";
import bn_notes from "./locales/bn/notes.json";
import bn_weather from "./locales/bn/weather.json";
import bn_prayer from "./locales/bn/prayer.json";
import bn_account from "./locales/bn/account.json";
import bn_auth from "./locales/bn/auth.json";
import bn_tour from "./locales/bn/tour.json";
import bn_location from "./locales/bn/location.json";
import bn_barakah from "./locales/bn/barakah.json";
import ms_common from "./locales/ms/common.json";
import ms_quran from "./locales/ms/quran.json";
import ms_todos from "./locales/ms/todos.json";
import ms_notes from "./locales/ms/notes.json";
import ms_weather from "./locales/ms/weather.json";
import ms_prayer from "./locales/ms/prayer.json";
import ms_account from "./locales/ms/account.json";
import ms_auth from "./locales/ms/auth.json";
import ms_tour from "./locales/ms/tour.json";
import ms_location from "./locales/ms/location.json";
import ms_barakah from "./locales/ms/barakah.json";
import ha_common from "./locales/ha/common.json";
import ha_quran from "./locales/ha/quran.json";
import ha_todos from "./locales/ha/todos.json";
import ha_notes from "./locales/ha/notes.json";
import ha_weather from "./locales/ha/weather.json";
import ha_prayer from "./locales/ha/prayer.json";
import ha_account from "./locales/ha/account.json";
import ha_auth from "./locales/ha/auth.json";
import ha_tour from "./locales/ha/tour.json";
import ha_location from "./locales/ha/location.json";
import ha_barakah from "./locales/ha/barakah.json";
import sw_common from "./locales/sw/common.json";
import sw_quran from "./locales/sw/quran.json";
import sw_todos from "./locales/sw/todos.json";
import sw_notes from "./locales/sw/notes.json";
import sw_weather from "./locales/sw/weather.json";
import sw_prayer from "./locales/sw/prayer.json";
import sw_account from "./locales/sw/account.json";
import sw_auth from "./locales/sw/auth.json";
import sw_tour from "./locales/sw/tour.json";
import sw_location from "./locales/sw/location.json";
import sw_barakah from "./locales/sw/barakah.json";
import it_common from "./locales/it/common.json";
import it_quran from "./locales/it/quran.json";
import it_todos from "./locales/it/todos.json";
import it_notes from "./locales/it/notes.json";
import it_weather from "./locales/it/weather.json";
import it_prayer from "./locales/it/prayer.json";
import it_account from "./locales/it/account.json";
import it_auth from "./locales/it/auth.json";
import it_tour from "./locales/it/tour.json";
import it_location from "./locales/it/location.json";
import it_barakah from "./locales/it/barakah.json";
import es_common from "./locales/es/common.json";
import es_quran from "./locales/es/quran.json";
import es_todos from "./locales/es/todos.json";
import es_notes from "./locales/es/notes.json";
import es_weather from "./locales/es/weather.json";
import es_prayer from "./locales/es/prayer.json";
import es_account from "./locales/es/account.json";
import es_auth from "./locales/es/auth.json";
import es_tour from "./locales/es/tour.json";
import es_location from "./locales/es/location.json";
import es_barakah from "./locales/es/barakah.json";
import sq_common from "./locales/sq/common.json";
import sq_quran from "./locales/sq/quran.json";
import sq_todos from "./locales/sq/todos.json";
import sq_notes from "./locales/sq/notes.json";
import sq_weather from "./locales/sq/weather.json";
import sq_prayer from "./locales/sq/prayer.json";
import sq_account from "./locales/sq/account.json";
import sq_auth from "./locales/sq/auth.json";
import sq_tour from "./locales/sq/tour.json";
import sq_location from "./locales/sq/location.json";
import sq_barakah from "./locales/sq/barakah.json";
import ru_common from "./locales/ru/common.json";
import ru_quran from "./locales/ru/quran.json";
import ru_todos from "./locales/ru/todos.json";
import ru_notes from "./locales/ru/notes.json";
import ru_weather from "./locales/ru/weather.json";
import ru_prayer from "./locales/ru/prayer.json";
import ru_account from "./locales/ru/account.json";
import ru_auth from "./locales/ru/auth.json";
import ru_tour from "./locales/ru/tour.json";
import ru_location from "./locales/ru/location.json";
import ru_barakah from "./locales/ru/barakah.json";

const resources: Resource = {
  en: {
    common: en_common,
    quran: en_quran,
    todos: en_todos,
    notes: en_notes,
    weather: en_weather,
    prayer: en_prayer,
    account: en_account,
    auth: en_auth,
    tour: en_tour,
    location: en_location,
    barakah: en_barakah,
  },
  ar: {
    common: ar_common,
    quran: ar_quran,
    todos: ar_todos,
    notes: ar_notes,
    weather: ar_weather,
    prayer: ar_prayer,
    account: ar_account,
    auth: ar_auth,
    tour: ar_tour,
    location: ar_location,
    barakah: ar_barakah,
  },
  ur: {
    common: ur_common,
    quran: ur_quran,
    todos: ur_todos,
    notes: ur_notes,
    weather: ur_weather,
    prayer: ur_prayer,
    account: ur_account,
    auth: ur_auth,
    tour: ur_tour,
    location: ur_location,
    barakah: ur_barakah,
  },
  fa: {
    common: fa_common,
    quran: fa_quran,
    todos: fa_todos,
    notes: fa_notes,
    weather: fa_weather,
    prayer: fa_prayer,
    account: fa_account,
    auth: fa_auth,
    tour: fa_tour,
    location: fa_location,
    barakah: fa_barakah,
  },
  id: {
    common: id_common,
    quran: id_quran,
    todos: id_todos,
    notes: id_notes,
    weather: id_weather,
    prayer: id_prayer,
    account: id_account,
    auth: id_auth,
    tour: id_tour,
    location: id_location,
    barakah: id_barakah,
  },
  tr: {
    common: tr_common,
    quran: tr_quran,
    todos: tr_todos,
    notes: tr_notes,
    weather: tr_weather,
    prayer: tr_prayer,
    account: tr_account,
    auth: tr_auth,
    tour: tr_tour,
    location: tr_location,
    barakah: tr_barakah,
  },
  fr: {
    common: fr_common,
    quran: fr_quran,
    todos: fr_todos,
    notes: fr_notes,
    weather: fr_weather,
    prayer: fr_prayer,
    account: fr_account,
    auth: fr_auth,
    tour: fr_tour,
    location: fr_location,
    barakah: fr_barakah,
  },
  bn: {
    common: bn_common,
    quran: bn_quran,
    todos: bn_todos,
    notes: bn_notes,
    weather: bn_weather,
    prayer: bn_prayer,
    account: bn_account,
    auth: bn_auth,
    tour: bn_tour,
    location: bn_location,
    barakah: bn_barakah,
  },
  ms: {
    common: ms_common,
    quran: ms_quran,
    todos: ms_todos,
    notes: ms_notes,
    weather: ms_weather,
    prayer: ms_prayer,
    account: ms_account,
    auth: ms_auth,
    tour: ms_tour,
    location: ms_location,
    barakah: ms_barakah,
  },
  ha: {
    common: ha_common,
    quran: ha_quran,
    todos: ha_todos,
    notes: ha_notes,
    weather: ha_weather,
    prayer: ha_prayer,
    account: ha_account,
    auth: ha_auth,
    tour: ha_tour,
    location: ha_location,
    barakah: ha_barakah,
  },
  sw: {
    common: sw_common,
    quran: sw_quran,
    todos: sw_todos,
    notes: sw_notes,
    weather: sw_weather,
    prayer: sw_prayer,
    account: sw_account,
    auth: sw_auth,
    tour: sw_tour,
    location: sw_location,
    barakah: sw_barakah,
  },
  it: {
    common: it_common,
    quran: it_quran,
    todos: it_todos,
    notes: it_notes,
    weather: it_weather,
    prayer: it_prayer,
    account: it_account,
    auth: it_auth,
    tour: it_tour,
    location: it_location,
    barakah: it_barakah,
  },
  es: {
    common: es_common,
    quran: es_quran,
    todos: es_todos,
    notes: es_notes,
    weather: es_weather,
    prayer: es_prayer,
    account: es_account,
    auth: es_auth,
    tour: es_tour,
    location: es_location,
    barakah: es_barakah,
  },
  sq: {
    common: sq_common,
    quran: sq_quran,
    todos: sq_todos,
    notes: sq_notes,
    weather: sq_weather,
    prayer: sq_prayer,
    account: sq_account,
    auth: sq_auth,
    tour: sq_tour,
    location: sq_location,
    barakah: sq_barakah,
  },
  ru: {
    common: ru_common,
    quran: ru_quran,
    todos: ru_todos,
    notes: ru_notes,
    weather: ru_weather,
    prayer: ru_prayer,
    account: ru_account,
    auth: ru_auth,
    tour: ru_tour,
    location: ru_location,
    barakah: ru_barakah,
  },
};

/** Namespaces — must match the JSON file names under each locale dir. */
export const NAMESPACES = [
  "common",
  "quran",
  "todos",
  "notes",
  "weather",
  "prayer",
  "account",
  "auth",
  "barakah",
  "tour",
  // First-run gate (location primer, manual city entry, the Location row).
  "location",
] as const;

const detected = detectLocale();

i18n.use(initReactI18next).init({
  resources,
  lng: detected,
  fallbackLng: DEFAULT_LOCALE,
  supportedLngs: SUPPORTED_LOCALES as unknown as string[],
  ns: NAMESPACES as unknown as string[],
  defaultNS: "common",
  returnEmptyString: false,
  interpolation: { escapeValue: false }, // React already escapes
  react: { useSuspense: false },
  saveMissing: process.env.NODE_ENV === "development",
  missingKeyHandler:
    process.env.NODE_ENV === "development"
      ? (lngs, ns, key) => {
          // eslint-disable-next-line no-console
          console.warn(`[i18n] missing key: ${ns}:${key} (${lngs.join(",")})`);
        }
      : undefined,
});

// Keep <html lang>/<dir> in sync.
applyDocumentLocale(detected);
i18n.on("languageChanged", applyDocumentLocale);

export default i18n;
